import {
  createFrontendPlugin,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import InsertChartOutlinedIcon from '@material-ui/icons/InsertChartOutlined';

/**
 * What kind of change lands on each service, from conventional-commit
 * prefixes. The backend plugin (packages/backend/src/plugins/workMix.ts)
 * ingests and classifies; this is the reading side.
 *
 * Deliberately no per-team roll-up and no org trend line, and neither is an
 * omission to be filled in later. Between-team variance here is dominated by
 * confounds no team controls - repo shape, PR-splitting culture, infra work
 * booked to infra on behalf of everyone - and with five teams any
 * presentation reads as a ranking of people off a field they self-report.
 * A trend would confound with the rollout of the PR-title check itself,
 * which is still changing per repo. See docs/work-mix.md.
 */

/**
 * A page reaches the sidebar only if it carries a route ref: AppNav walks the
 * app's route nodes and drops any whose `core.routing.ref` is absent, however
 * complete its title and icon are.
 */
const workMixRouteRef = createRouteRef();

export const workMixPage = PageBlueprint.make({
  params: {
    path: '/work-mix',
    routeRef: workMixRouteRef,
    icon: <InsertChartOutlinedIcon fontSize="inherit" />,
    title: 'Work mix',
    loader: async () => import('./WorkMixPage').then(m => <m.WorkMixPage />),
  },
});

/**
 * On components and systems only. A module gets its deployable's figure,
 * labelled as inherited - commits cannot be attributed below the repo.
 */
export const workMixCard = EntityCardBlueprint.make({
  name: 'work-mix',
  params: {
    filter: entity =>
      ['component', 'system'].includes(entity.kind.toLocaleLowerCase('en-US')),
    loader: async () => import('./WorkMixCard').then(m => <m.WorkMixCard />),
  },
});

/**
 * On a team's page: every service it owns, each with its own figure and no
 * total. The refusal of a per-team roll-up (docs/work-mix.md) is about one
 * number standing for a team; a list of its services says only what each
 * service's own page says, in one place.
 */
export const teamWorkMixCard = EntityCardBlueprint.make({
  name: 'team-work-mix',
  params: {
    type: 'info',
    filter: { kind: 'group' },
    loader: async () =>
      import('./TeamWorkMixCard').then(m => <m.TeamWorkMixCard />),
  },
});

export const workMixPlugin = createFrontendPlugin({
  pluginId: 'work-mix',
  routes: { workMix: workMixRouteRef },
  extensions: [workMixPage, workMixCard, teamWorkMixCard],
});
